import {useCallback, useEffect, useMemo, useState} from 'react';
import {createEntityAlias, deleteEntityAlias} from '../firebase/firestore/entityAliasMutations';

function mapAliasMutationError(err) {
    const message = err?.message || 'Failed to update aliases.';
    const wrappedError = new Error(message);
    wrappedError.code = err?.code || 'unknown';
    wrappedError.cause = err;
    return wrappedError;
}

export function useEntityAliasMutations({entityId, onMutationSuccess}) {
    const [activeOperations, setActiveOperations] = useState(0);
    const [error, setError] = useState(null);

    useEffect(() => {
        setError(null);
    }, [entityId]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const runMutation = useCallback(async (runner) => {
        setActiveOperations((count) => count + 1);
        setError(null);

        try {
            const result = await runner();
            onMutationSuccess?.(result);
            return result;
        } catch (err) {
            const wrappedError = mapAliasMutationError(err);
            setError(wrappedError);
            throw wrappedError;
        } finally {
            setActiveOperations((count) => Math.max(0, count - 1));
        }
    }, [onMutationSuccess]);

    const createAlias = useCallback(async (alias) => {
        return runMutation(() => createEntityAlias({entityId, alias}));
    }, [entityId, runMutation]);

    const deleteAlias = useCallback(async (alias) => {
        return runMutation(() => deleteEntityAlias({entityId, alias}));
    }, [entityId, runMutation]);

    return useMemo(() => ({
        createAlias,
        deleteAlias,
        saving: activeOperations > 0,
        error,
        clearError,
    }), [activeOperations, clearError, createAlias, deleteAlias, error]);
}
